import { useCallback, useEffect, useMemo, useState } from "react";
import { getFollowUpsByOrganizationAsync } from "../api/FollowUpApi";
import { FollowUp, FollowUpStatus } from "../models/FollowUpModels";
import { PagedResult } from "../models/PagedModels";
import { Constants } from "../constants/constants";
import { monthRange } from "../utils/DateTime";

const DEFAULT_PAGE_SIZE = 20;

export interface FollowUpFilters {
  status?: FollowUpStatus;
  dueOnly?: boolean;
  search?: string;
  dueFrom?: string;
  dueTo?: string;
}

interface UseFollowUpsReturn {
  followUps: FollowUp[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  filters: FollowUpFilters;
  setFilters: (filters: FollowUpFilters) => void;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
}

export const useFollowUps = (
  organizationId: string | undefined,
): UseFollowUpsReturn => {
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<FollowUpFilters>(() => ({
    dueOnly: false,
    ...monthRange(),
  }));
  const [data, setData] = useState<PagedResult<FollowUp> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!organizationId || !token) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getFollowUpsByOrganizationAsync(token, organizationId, {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        status: filters.status,
        search: filters.search?.trim() || undefined,
        isDue: filters.dueOnly || undefined,
        dueFrom: filters.dueFrom,
        dueTo: filters.dueTo,
      });
      if (res.success && res.result) {
        setData(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load follow-ups");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    token,
    page,
    filters.status,
    filters.dueOnly,
    filters.search,
    filters.dueFrom,
    filters.dueTo,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilters = useCallback((next: FollowUpFilters) => {
    setPage(1);
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  const reload = useCallback(async () => {
    setPage(1);
    await load();
  }, [load]);

  const followUps = useMemo(() => data?.items ?? [], [data]);

  return {
    followUps,
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? DEFAULT_PAGE_SIZE,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    filters,
    setFilters,
    setPage,
    reload,
  };
};